import React, { createContext, useContext, useState, useEffect } from "react";

export interface AuthUser {
  username: string;
  role: "admin" | "user";
  provider: "google" | "github" | "credentials";
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthModalOpen: boolean;
  setAuthModalOpen: (open: boolean) => void;
  loginPasswordless: (provider: "google" | "github", username: string) => Promise<void>;
  validateAdminPassword: (password: string) => Promise<{ success: boolean; error?: string }>;
  loginAdmin: (password: string, code: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const ADMIN_USERNAME = "admin";


// ==========================================
// API BOUNDARIES (PREPARED FOR RUST BACKEND)
// ==========================================

export async function apiValidateAdminPassword(
  password: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const res = await fetch("/api/admin/verify-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    });
    
    if (res.ok) {
      const data = await res.json();
      return { success: data.success };
    } else {
      const errText = await res.text();
      return { success: false, error: errText || "Invalid administrative password" };
    }
  } catch (e) {
    return { success: false, error: "Authentication server connection error" };
  }
}

export async function apiValidateAdminMFA(
  password: string,
  code: string
): Promise<{ success: boolean; user?: AuthUser; token?: string; error?: string }> {
  try {
    const res = await fetch("/api/admin/verify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password, code }),
    });

    if (res.ok) {
      const data = await res.json();
      if (data.success) {
        return { success: true, user: data.user, token: data.token };
      } else {
        return { success: false, error: data.error || "Invalid MFA verification code" };
      }
    } else {
      return { success: false, error: "Administrative authentication failed" };
    }
  } catch (e) {
    return { success: false, error: "MFA server connection error" };
  }
}

export async function apiValidatePasswordless(
  provider: "google" | "github",
  username: string
): Promise<{ success: boolean; user?: AuthUser; error?: string }> {
  // Simulate network delay
  await new Promise((resolve) => setTimeout(resolve, 1200));

  return {
    success: true,
    user: {
      username: username || (provider === "google" ? "Google User" : "GitHub Dev"),
      role: "user",
      provider
    }
  };
}

// ==========================================
// CONTEXT PROVIDER
// ==========================================

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isAuthModalOpen, setAuthModalOpen] = useState(false);

  // Restore session from localStorage on mount
  useEffect(() => {
    const savedSession = localStorage.getItem("caio_session");
    if (savedSession) {
      try {
        setUser(JSON.parse(savedSession));
      } catch (e) {
        localStorage.removeItem("caio_session");
      }
    }
  }, []);

  const loginPasswordless = async (provider: "google" | "github", username: string) => {
    const response = await apiValidatePasswordless(provider, username);
    if (response.success && response.user) {
      setUser(response.user);
      localStorage.setItem("caio_session", JSON.stringify(response.user));
    } else {
      throw new Error(response.error || "Passwordless authentication failed");
    }
  };

  const validateAdminPassword = async (password: string): Promise<{ success: boolean; error?: string }> => {
    return await apiValidateAdminPassword(password);
  };

  const loginAdmin = async (password: string, code: string): Promise<{ success: boolean; error?: string }> => {
    const response = await apiValidateAdminMFA(password, code);
    if (response.success && response.user) {
      setUser(response.user);
      localStorage.setItem("caio_session", JSON.stringify(response.user));
      if (response.token) {
        localStorage.setItem("admin_token", response.token);
      }
      return { success: true };
    } else {
      return { success: false, error: response.error || "Administrative authentication failed" };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("caio_session");
    localStorage.removeItem("admin_token");
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthModalOpen,
        setAuthModalOpen,
        loginPasswordless,
        validateAdminPassword,
        loginAdmin,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
